import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "../templates/ui/Card";
import Button from "../templates/ui/Button";
import CenterPopup from "../templates/ui/CenterPopup";
import CenterToast from "../templates/ui/CenterToast";
import {
  buildApiErrorPopupTemplate,
  buildApiSuccessPopupTemplate,
  buildMeritCompletePopupTemplate,
} from "../templates/popupTemplate";
import { muteluApi } from "../services/muteluApi";
import {
  logPageApiCall,
  logPageApiError,
  logPageApiResult,
} from "../utils/apiDebugLogger";
import meritDayImage from "../../../assets/images/game-mutelu/day.png";
import silverWaterBowlImage from "../../../assets/images/game-mutelu/silver-water-bowl.png";
import lineOfficialIcon from "../../../assets/images/game-mutelu/line-app-logo.png";
import "./MeritBath.css";
import { useCampaignStore } from "../app/store/campaignStore";

const weekLabels = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];
const CONNECTION_ERROR_MESSAGE =
  "ไม่สามารถเชื่อมต่อกับระบบ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต";
const POUR_ANIMATION_MS = 5000;
const FORCE_TICKET_REFRESH_KEY = "mutelu:force-ticket-refresh";
const TICKET_BALANCE_SNAPSHOT_KEY = "mutelu:ticket-balance:snapshot";
const normalizeMessage = (value, fallback = "") => {
  if (typeof value === "string") return value;
  if (value == null) return fallback;
  if (typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    if (typeof value.data?.message === "string") return value.data.message;
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return String(value);
};
const isConnectionError = (message) =>
  /ไม่สามารถเชื่อมต่อ|ตรวจสอบอินเทอร์เน็ต|network|timeout/i.test(
    String(message || ""),
  );
const containsSensitiveErrorDetail = (message) =>
  /(insert into|update\s+"|delete from|on conflict|violates check constraint|pq:|sql|syntax error|relation ")/i.test(
    String(message || ""),
  );
const sanitizePopupErrorMessage = (message, fallback) => {
  const normalized = normalizeMessage(message, fallback);
  if (containsSensitiveErrorDetail(normalized)) {
    return fallback;
  }
  return normalized;
};
const formatMonthKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};
const getMonthMeta = (monthKey) => {
  const [year, month] = String(monthKey)
    .split("-")
    .map((value) => Number(value));
  const firstDayDate = new Date(year, month - 1, 1);
  const offset = firstDayDate.getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  return { offset, daysInMonth };
};
const toSafeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const normalizeDisplayName = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  if (normalized === "ผู้เล่น") return "";
  return normalized;
};
const isRoomNotFoundError = (error) => {
  const code = String(
    error?.data?.error_code || error?.errorCode || "",
  ).toUpperCase();
  if (code === "ROOM_NOT_FOUND" || code === "GROUP_NOT_FOUND") return true;
  if (Number(error?.status || error?.response?.status) === 404) return true;
  const message = String(error?.data?.message || error?.message || "");
  return /room not found|group not found|ไม่พบห้อง|ไม่พบปาร์ตี้/i.test(message);
};
const FETCH_DEDUPE_MS = 1200;
const normalizeMeritMembers = (value) => {
  const list = Array.isArray(value) ? value : [];
  const sorted = [...list].sort((a, b) => {
    const ownerA = a?.is_owner ? 1 : 0;
    const ownerB = b?.is_owner ? 1 : 0;
    if (ownerA !== ownerB) return ownerB - ownerA;
    const pouredA = a?.poured_today ? 1 : 0;
    const pouredB = b?.poured_today ? 1 : 0;
    return pouredB - pouredA;
  });
  const seen = new Set();
  const normalized = [];
  sorted.forEach((member, index) => {
    const dedupeKey =
      member?.line_mid ||
      member?.line_id ||
      member?.player_id ||
      (typeof member?.display_name === "string"
        ? member.display_name.trim()
        : "") ||
      `member-${index + 1}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    normalized.push(member);
  });
  return normalized.slice(0, 4);
};

const sleep = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

export default function MeritBath() {
  const location = useLocation();
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { state } = useCampaignStore();
  const monthKey = useMemo(() => formatMonthKey(new Date()), []);
  const [activeRoomCode, setActiveRoomCode] = useState(roomId || "");
  const [roomTitle, setRoomTitle] = useState(
    location.state?.roomTitle || "ห้องตี้ของคุณ",
  );
  const [pouring, setPouring] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [calendarData, setCalendarData] = useState({
    month: monthKey,
    members_count: 0,
    days: [],
  });
  const [loading, setLoading] = useState(true);
  const [rewardPopupTemplate, setRewardPopupTemplate] = useState(null);
  const [redirectToActivityAfterPopup, setRedirectToActivityAfterPopup] =
    useState(false);
  const [copyToast, setCopyToast] = useState("");
  const [apiPopupTemplate, setApiPopupTemplate] = useState(null);
  const copyToastTimerRef = useRef(null);
  const lastFetchRef = useRef({ key: "", timestamp: 0 });
  const bathDone = Boolean(statusData?.bath_done_today);
  const filledSegments = Math.min(4, toSafeNumber(statusData?.filled_segments));
  const meritTargetCount = Math.max(
    2,
    Math.min(4, toSafeNumber(statusData?.members_count) || 4),
  );
  const members = Array.isArray(statusData?.members) ? statusData.members : [];
  const bathChecklist = members.map((member, index) => ({
    id: member?.line_mid || `member-${index + 1}`,
    name: member?.is_owner
      ? `${normalizeDisplayName(member?.display_name) || ""} (หัวหน้าตี้)`.trim()
      : normalizeDisplayName(member?.display_name) || "",
    done: Boolean(member?.poured_today),
  }));
  const doneCount = bathChecklist.filter((member) => member.done).length;
  const dayMap = useMemo(
    () =>
      new Map(
        (Array.isArray(calendarData?.days) ? calendarData.days : []).map(
          (item) => [item.day, item],
        ),
      ),
    [calendarData],
  );
  const { offset: calendarOffset, daysInMonth } = getMonthMeta(
    calendarData?.month || monthKey,
  );
  const monthDays = Array.from(
    { length: daysInMonth },
    (_, index) => index + 1,
  );
  const openApiErrorPopup = useCallback((title, subtitle, message) => {
    const fallbackMessage = "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
    const normalized = sanitizePopupErrorMessage(
      message,
      fallbackMessage,
    );
    if (isConnectionError(normalized)) {
      setCopyToast(CONNECTION_ERROR_MESSAGE);
      if (copyToastTimerRef.current) {
        window.clearTimeout(copyToastTimerRef.current);
      }
      copyToastTimerRef.current = window.setTimeout(() => {
        setCopyToast("");
      }, 1700);
      return;
    }
    setApiPopupTemplate(
      buildApiErrorPopupTemplate({
        title,
        subtitle,
        message: normalized,
      }),
    );
  }, []);

  const fetchMeritData = useCallback(
    async (groupCode) => {
      if (!groupCode) return;
      const fetchKey = `${groupCode}:${monthKey}`;
      const now = Date.now();
      if (
        lastFetchRef.current.key === fetchKey &&
        now - lastFetchRef.current.timestamp < FETCH_DEDUPE_MS
      ) {
        return;
      }
      lastFetchRef.current = { key: fetchKey, timestamp: now };
      logPageApiCall("MeritBath", "getMeritRoomStatus + getMeritCalendar", {
        groupCode,
        monthKey,
      });
      const [statusRes, calendarRes] = await Promise.all([
        muteluApi.getMeritRoomStatus(groupCode),
        muteluApi.getMeritCalendar(groupCode, monthKey),
      ]);
      const statusPayload =
        statusRes?.data && typeof statusRes.data === "object"
          ? statusRes.data
          : statusRes;
      const calendarPayload =
        calendarRes?.data && typeof calendarRes.data === "object"
          ? calendarRes.data
          : calendarRes;
      const normalizedMembers = normalizeMeritMembers(statusPayload?.members);
      setStatusData(
        statusPayload
          ? {
              ...statusPayload,
              members: normalizedMembers,
              members_count:
                toSafeNumber(statusPayload?.members_count) ||
                normalizedMembers.length,
            }
          : null,
      );
      setRoomTitle(
        statusPayload?.room_name ||
          location.state?.roomTitle ||
          `Party ${groupCode}`,
      );
      setCalendarData({
        month: calendarPayload?.month || monthKey,
        members_count: toSafeNumber(calendarPayload?.members_count),
        days: Array.isArray(calendarPayload?.days) ? calendarPayload.days : [],
      });
      logPageApiResult("MeritBath", "getMeritRoomStatus", statusPayload);
      logPageApiResult("MeritBath", "getMeritCalendar", calendarPayload);
    },
    [location.state?.roomTitle, monthKey],
  );

  useEffect(() => {
    return () => {
      if (copyToastTimerRef.current) {
        window.clearTimeout(copyToastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setActiveRoomCode(roomId || "");

    const bootstrap = async () => {
      setLoading(true);
      try {
        const candidateCode = roomId || location.state?.roomId;
        logPageApiCall("MeritBath", "getMeritRooms");
        const roomsResponse = await muteluApi.getMeritRooms();
        logPageApiResult("MeritBath", "getMeritRooms", roomsResponse);
        const rooms = Array.isArray(roomsResponse?.data)
          ? roomsResponse.data
          : [];

        if (rooms.length === 0) {
          openApiErrorPopup(
            "ไม่พบห้องสรงน้ำ",
            "หน้า Merit Bath",
            "ไม่พบห้องสรงน้ำที่ใช้งานอยู่ในปาร์ตี้นี้ กรุณากลับไปหน้า Activity",
          );
          return;
        }

        const normalizedCandidate = String(candidateCode || "")
          .trim()
          .toUpperCase();
        const matchedRoom = normalizedCandidate
          ? rooms.find(
              (room) =>
                String(room?.room_id || "")
                  .trim()
                  .toUpperCase() === normalizedCandidate,
            )
          : null;
        const targetRoom = matchedRoom || rooms[0];

        if (!targetRoom?.room_id) {
          openApiErrorPopup(
            "ไม่พบห้องสรงน้ำ",
            "หน้า Merit Bath",
            "ไม่พบห้องสรงน้ำที่ใช้งานอยู่ในปาร์ตี้นี้ กรุณากลับไปหน้า Activity",
          );
          return;
        }

        if (!mounted) return;
        if (normalizedCandidate && !matchedRoom) {
          navigate(`/game/mutelu/merit-bath/${targetRoom.room_id}`, {
            replace: true,
            state: { roomTitle: targetRoom.room_name },
          });
          return;
        }

        setActiveRoomCode(targetRoom.room_id);
        await fetchMeritData(targetRoom.room_id);
      } catch (error) {
        logPageApiError("MeritBath", "bootstrap merit data", error);
        if (!mounted) return;
        if (isRoomNotFoundError(error)) {
          try {
            logPageApiCall(
              "MeritBath",
              "recover -> getMeritRooms after room not found",
            );
            const recoverRoomsResponse = await muteluApi.getMeritRooms();
            logPageApiResult(
              "MeritBath",
              "recover -> getMeritRooms after room not found",
              recoverRoomsResponse,
            );
            const recoverRooms = Array.isArray(recoverRoomsResponse?.data)
              ? recoverRoomsResponse.data
              : [];
            const recoverRoom = recoverRooms[0];
            if (recoverRoom?.room_id) {
              navigate(`/game/mutelu/merit-bath/${recoverRoom.room_id}`, {
                replace: true,
                state: { roomTitle: recoverRoom.room_name },
              });
              return;
            }
            openApiErrorPopup(
              "ไม่พบห้องสรงน้ำ",
              "หน้า Merit Bath",
              "ไม่พบห้องสรงน้ำที่ใช้งานอยู่ในปาร์ตี้นี้ กรุณากลับไปหน้า Activity",
            );
            return;
          } catch (recoverError) {
            logPageApiError(
              "MeritBath",
              "recover merit room failed",
              recoverError,
            );
            openApiErrorPopup(
              "ไม่พบห้องสรงน้ำ",
              "หน้า Merit Bath",
              "ไม่สามารถกู้ข้อมูลห้องสรงน้ำได้ กรุณากลับไปหน้า Activity",
            );
            return;
          }
        }
        openApiErrorPopup(
          "โหลดข้อมูลสรงน้ำไม่สำเร็จ",
          "หน้า Merit Bath",
          error?.data?.message || error?.message,
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      mounted = false;
    };
  }, [
    fetchMeritData,
    location.state?.roomId,
    monthKey,
    navigate,
    openApiErrorPopup,
    roomId,
  ]);

  const onBath = () => {
    if (bathDone || pouring || !activeRoomCode) {
      if (bathDone) navigate("/game/mutelu/activity");
      return;
    }
    setPouring(true);
    const animationStartTime = Date.now();

    const submit = async () => {
      try {
        logPageApiCall("MeritBath", "pourMeritRoom", {
          roomCode: activeRoomCode,
          source: "mutelu-web",
        });
        const response = await muteluApi.pourMeritRoom(activeRoomCode, {
          source: "mutelu-web",
        });
        logPageApiResult("MeritBath", "pourMeritRoom", response);
        const payload =
          response?.data && typeof response.data === "object"
            ? response.data
            : response;
        const reward = payload?.daily_completion_reward || null;
        const ticketBalance = payload?.ticket_balance || null;
        if (ticketBalance && typeof ticketBalance === "object") {
          localStorage.setItem(
            TICKET_BALANCE_SNAPSHOT_KEY,
            JSON.stringify(ticketBalance),
          );
          localStorage.setItem(FORCE_TICKET_REFRESH_KEY, String(Date.now()));
        }
        await fetchMeritData(activeRoomCode);
        const amount = Number(reward?.ticket_amount || 0);
        const elapsed = Date.now() - animationStartTime;
        const remainingAnimation = Math.max(0, POUR_ANIMATION_MS - elapsed);
        if (remainingAnimation > 0) {
          await sleep(remainingAnimation);
        }
        if (reward?.completed_today && amount > 0) {
          setRewardPopupTemplate({
            icon: "✅",
            title: `ได้รับตั๋วทองแดง คนละ ${amount} ใบ`,
            subtitle: "สมาชิกสรงน้ำครบทั้งตี้แล้ว",
            message: "",
            confirmLabel: "กลับหน้า Activity",
            cancelLabel: "",
          });
          setRedirectToActivityAfterPopup(true);
          return;
        }
        setRewardPopupTemplate(
          buildApiSuccessPopupTemplate({
            title: "สรงน้ำสำเร็จ",
            subtitle: "บันทึกการสรงน้ำของคุณเรียบร้อยแล้ว",
            message:
              "ชวนกันสรงน้ำให้ครบทั้ง 4 คนในวันเดียวกัน รับตั๋วทองแดง พร้อมโบนัสคูณรางวัลตามจำนวน New Member",
          }),
        );
        setRedirectToActivityAfterPopup(true);
      } catch (error) {
        logPageApiError("MeritBath", "pourMeritRoom", error);
        const message = normalizeMessage(
          error?.data?.message || error?.message,
          "สรงน้ำไม่สำเร็จ",
        );
        if (
          /already poured|already_poured|already poured today|ALREADY_POURED/i.test(
            message,
          )
        ) {
          await fetchMeritData(activeRoomCode);
          showCopyToast("สรงน้ำแล้ววันนี้");
          return;
        }
        openApiErrorPopup("สรงน้ำไม่สำเร็จ", "หน้า Merit Bath", message);
      } finally {
        setPouring(false);
      }
    };
    submit();
  };

  const onShareLine = () => {
    try {
      const shareUrl = encodeURIComponent(window.location.href);
      const shareText = encodeURIComponent(
        "ชวนมาร่วมภารกิจสรงน้ำมูเตลู ด้วยกันที่ Big C 🌼",
      );
      const popup = window.open(
        `https://line.me/R/msg/text/?${shareText}%20${shareUrl}`,
        "_blank",
        "noopener,noreferrer",
      );
      if (!popup) {
        showCopyToast("share link แชร์ไม่สำเร็จ กรุณาลองอีกครั้ง");
        return;
      }
      showCopyToast("กำลังเปิด LINE เพื่อแชร์");
    } catch {
      showCopyToast("share link แชร์ไม่สำเร็จ กรุณาลองอีกครั้ง");
    }
  };

  const copyToClipboard = async (value) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch {
        // fallback to execCommand
      }
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      return copied;
    } catch {
      return false;
    }
  };

  const showCopyToast = (message) => {
    setCopyToast(message);
    if (copyToastTimerRef.current) {
      window.clearTimeout(copyToastTimerRef.current);
    }
    copyToastTimerRef.current = window.setTimeout(() => {
      setCopyToast("");
    }, 1600);
  };

  const onCopyLink = async () => {
    const isCopied = await copyToClipboard(window.location.href);
    showCopyToast(isCopied ? "คัดลอกลิงก์แล้ว" : "คัดลอกลิงก์ไม่สำเร็จ");
    if (isCopied) {
      return;
    }
    try {
      window.prompt("คัดลอกลิงก์นี้", window.location.href);
    } catch {}
  };

  return (
    <section className="mobile-page merit-page mx-auto w-full max-w-md">
      <Card className="mobile-panel merit-card">
        <div className="landing-header px-4 py-4 md:px-6">
          <p className="landing-badge">Big C Receipt Mutelu</p>
          <h1 className="landing-title mt-1 md:text-3xl">
            {state.campaign.campaignName}
          </h1>
          <p className="landing-subtitle mt-1">ภารกิจสรงน้ำสะสมบุญ</p>
        </div>

        <div className="merit-room-status">
          <p className="merit-room-title">ห้อง: {roomTitle}</p>
        </div>

        <div className="merit-body">
          {/* <p className="merit-title">Daily Retention</p>
          <p className="merit-sub">
          
          </p> */}

          <div className="merit-game">
            <div
              className={`merit-ring ${pouring ? "is-pouring" : ""} ${bathDone ? "is-complete" : ""}`}
            >
              <div
                className="merit-ring-segments"
                aria-label={`สรงน้ำแล้ว ${filledSegments} จาก ${meritTargetCount}`}
              >
                {Array.from({ length: 4 }).map((_, index) => (
                  <span
                    key={`segment-${index + 1}`}
                    className={`merit-segment segment-${index + 1} ${index < filledSegments ? "is-active" : ""}`}
                  />
                ))}
              </div>
              <div className="merit-ritual" aria-hidden="true">
                <img
                  src={silverWaterBowlImage}
                  alt=""
                  className="merit-water-bowl-image"
                />
                <div className="merit-water-stream merit-water-stream-main" />
                <div className="merit-water-stream merit-water-stream-left" />
                <div className="merit-water-stream merit-water-stream-right" />
                <div className="merit-center-art">
                  <img
                    src={meritDayImage}
                    alt="พิธีสรงน้ำ"
                    className="merit-center-art-image"
                    loading="eager"
                  />
                </div>
              </div>
              {/* <div className="merit-ring-center">
                <p className="merit-ring-label">Complete</p>
              </div> */}
              <div className="merit-spark merit-spark-1" />
              <div className="merit-spark merit-spark-2" />
              <div className="merit-spark merit-spark-3" />
            </div>
            {/* ซ่อนสถานะเพื่อนชั่วคราว */}
          </div>

          <Button
            className={`btn-uniform merit-btn-large ${bathDone ? "is-complete" : ""}`}
            onClick={onBath}
            disabled={bathDone || pouring || loading}
          >
            {bathDone
              ? "สรงน้ำแล้ววันนี้"
              : pouring
                ? "กำลังสรงน้ำ..."
                : "กดสรงน้ำสะสมบุญ"}
          </Button>

          <div className="merit-share-actions">
            <Button
              className="btn-uniform merit-share-btn merit-share-line px-5"
              onClick={onShareLine}
            >
              <span className="merit-share-icon" aria-hidden="true">
                <img
                  className="line-share-icon-image"
                  src={lineOfficialIcon}
                  alt=""
                />
              </span>
              <span>Share LINE</span>
            </Button>
            <Button
              className="btn-uniform merit-share-btn merit-share-copy px-5"
              onClick={onCopyLink}
            >
              <span>Copy link</span>
            </Button>
          </div>

          <section
            className="merit-checklist"
            aria-label="สถานะสมาชิกที่สรงน้ำแล้ว"
          >
            <div className="merit-checklist-head">
              <h2>เช็กลิสต์สรงน้ำวันนี้</h2>
              <p>
                {doneCount} /{" "}
                {bathChecklist.length ||
                  toSafeNumber(statusData?.members_count) ||
                  4}{" "}
                คน
              </p>
            </div>
            <div className="merit-checklist-list">
              {bathChecklist.map((member) => (
                <div
                  key={member.id}
                  className={`merit-checklist-item ${member.done ? "is-done" : "is-pending"}`}
                >
                  <span className="merit-check-icon" aria-hidden="true">
                    {member.done ? "✓" : "•"}
                  </span>
                  <span className="merit-check-name">{member.name}</span>
                  <span className="merit-check-status">
                    {member.done ? "สรงน้ำแล้ว" : "ยังไม่สรงน้ำ"}
                  </span>
                </div>
              ))}
              {!loading && bathChecklist.length === 0 ? (
                <div className="merit-checklist-item is-pending">
                  <span className="merit-check-icon" aria-hidden="true">
                    •
                  </span>
                  <span className="merit-check-name">
                    ยังไม่มีสมาชิกปาร์ตี้
                  </span>
                  <span className="merit-check-status">รอข้อมูล</span>
                </div>
              ) : null}
            </div>
          </section>

          <section className="merit-calendar" aria-label="ปฏิทินภารกิจสรงน้ำ">
            <div className="merit-calendar-head">
              <h2>ปฏิทินภารกิจสรงน้ำ</h2>
            </div>
            <div className="merit-calendar-weekdays">
              {weekLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            <div className="merit-calendar-grid">
              {Array.from({ length: calendarOffset }).map((_, index) => (
                <span
                  key={`empty-${index + 1}`}
                  className="merit-day empty"
                  aria-hidden="true"
                />
              ))}
              {monthDays.map((day) => {
                const dayStatus = dayMap.get(day);
                const isPass = dayStatus?.status === "pass";
                return (
                  <div
                    key={`day-${day}`}
                    className={`merit-day ${isPass ? "is-pass" : ""}`}
                  >
                    <span className="merit-day-number">{day}</span>
                    {isPass ? (
                      <span className="merit-stamp" aria-label="ผ่านภารกิจวันนี้">
                        <img
                          src={silverWaterBowlImage}
                          alt=""
                          className="merit-stamp-image"
                        />
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>

          <Button
            type="button"
            className="btn-uniform merit-back-activity-btn"
            onClick={() => navigate("/game/mutelu/activity")}
          >
            กลับหน้า Activity
          </Button>
        </div>
      </Card>

      <CenterPopup
        open={Boolean(rewardPopupTemplate)}
        template={rewardPopupTemplate || buildMeritCompletePopupTemplate()}
        onCancel={() => {
          setRewardPopupTemplate(null);
          if (redirectToActivityAfterPopup) {
            setRedirectToActivityAfterPopup(false);
            navigate("/game/mutelu/activity");
          }
        }}
        onConfirm={() => {
          setRewardPopupTemplate(null);
          if (redirectToActivityAfterPopup) {
            setRedirectToActivityAfterPopup(false);
            navigate("/game/mutelu/activity");
          }
        }}
      />
      <CenterToast open={Boolean(copyToast)} message={copyToast} />
      <CenterPopup
        open={Boolean(apiPopupTemplate)}
        template={apiPopupTemplate}
        onCancel={() => setApiPopupTemplate(null)}
        onConfirm={() => setApiPopupTemplate(null)}
      />
    </section>
  );
}
